// store/slices/cartSlice.ts
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { CartItem, CartState } from "../../common/types/types";
import { RootState } from "../store";

const initialState: CartState = {
  items: [],
  total: 0,
};

const calculateTotal = (items: CartItem[]): number => {
  return items.reduce((total, item) => total + item.price * item.quantity, 0);
};

const cartSlice = createSlice({
  name: "cart",
  initialState,
  reducers: {
    addToCart: (state, action: PayloadAction<Omit<CartItem, "quantity">>) => {
      const existingItem = state.items.find(
        (item: CartItem) =>
          item.id === action.payload.id &&
          item.isGift === action.payload.isGift,
      );

      if (existingItem) {
        existingItem.quantity += existingItem.quantityStep;
      } else {
        state.items.push({
          ...action.payload,
          quantity: action.payload.minQuantity,
        });
      }
      state.total = calculateTotal(state.items);
    },

    removeFromCart: (state, action: PayloadAction<string>) => {
      state.items = state.items.filter((item) => item.id !== action.payload);
      state.total = calculateTotal(state.items);
    },

    increaseQuantity: (state, action: PayloadAction<string>) => {
      const item = state.items.find(
        (item: CartItem) => item.id === action.payload,
      );
      if (item) {
        if (item.isGift) return;
        item.quantity += item.quantityStep;
        state.total = calculateTotal(state.items);
      }
    },

    decreaseQuantity: (state, action: PayloadAction<string>) => {
      const item = state.items.find((item) => item.id === action.payload);
      if (item) {
        if (item.isGift) return;
        const newQuantity = item.quantity - item.quantityStep;

        if (newQuantity < item.minQuantity) {
          state.items = state.items.filter((i) => i.id !== action.payload);
        } else {
          item.quantity = newQuantity;
        }
        state.total = calculateTotal(state.items);
      }
    },

    clearCart: (state) => {
      state.items = [];
      state.total = 0;
    },

    setCart: (state, action: PayloadAction<CartItem[]>) => {
      state.items = action.payload;
      state.total = calculateTotal(action.payload);
    },

    addGift: (state, action: PayloadAction<CartItem>) => {
      state.items = state.items.filter((item) => !item.isGift);
      state.items.push(action.payload);
      state.total = calculateTotal(state.items);
    },

    removeGift: (state) => {
      state.items = state.items.filter((item) => !item.isGift);
      state.total = calculateTotal(state.items);
    },
  },
});

export const {
  addToCart,
  removeFromCart,
  increaseQuantity,
  decreaseQuantity,
  clearCart,
  setCart,
  addGift,
  removeGift,
} = cartSlice.actions;

export const selectCart = (state: RootState) => state.cart;
export const selectCartItems = (state: RootState) => state.cart.items;

export default cartSlice.reducer;
